import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useLanguage } from '../../../contexts/LanguageContext';

const FinancialTab = ({ financial, onGenerateInvoice, onProcessPayment }) => {
  const { t, currentLanguage } = useLanguage();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(currentLanguage === 'fr' ? 'fr-MA' : 'ar-MA', {
      style: 'currency',
      currency: 'MAD'
    })?.format(amount);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: { color: 'bg-success/10 text-success', label: t('pilgrimProfile.paid') },
      pending: { color: 'bg-warning/10 text-warning', label: t('pilgrimProfile.pending') },
      refunded: { color: 'bg-error/10 text-error', label: t('pilgrimProfile.refunded') }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs md:text-sm font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const getPaymentMethodLabel = (method) => {
    const methods = {
      cash: t('pilgrimProfile.cash'),
      card: t('pilgrimProfile.card'),
      transfer: t('pilgrimProfile.transfer'),
      cheque: t('pilgrimProfile.cheque')
    };
    return methods?.[method] || method;
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h3 className="text-lg md:text-xl font-semibold text-foreground">
          {t('pilgrimProfile.financial')}
        </h3>
        <div className="flex flex-wrap gap-2 md:gap-3">
          <Button
            variant="outline"
            iconName="FileText"
            iconPosition="left"
            onClick={onGenerateInvoice}
          >
            {t('pilgrimProfile.generateInvoice')}
          </Button>
          <Button
            variant="default"
            iconName="CreditCard"
            iconPosition="left"
            onClick={onProcessPayment}
          >
            {t('pilgrimProfile.processPayment')}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        <div className="bg-card rounded-lg border border-border p-4 md:p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="DollarSign" size={20} className="text-primary" />
            </div>
            <div>
              <p className="caption text-muted-foreground">
                {t('pilgrimProfile.totalAmount')}
              </p>
              <p className="text-xl md:text-2xl font-bold text-foreground data-text">
                {formatCurrency(financial?.totalAmount)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-4 md:p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-success/10 flex items-center justify-center">
              <Icon name="CheckCircle" size={20} className="text-success" />
            </div>
            <div>
              <p className="caption text-muted-foreground">
                {t('pilgrimProfile.paidAmount')}
              </p>
              <p className="text-xl md:text-2xl font-bold text-success data-text">
                {formatCurrency(financial?.paidAmount)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-4 md:p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-warning/10 flex items-center justify-center">
              <Icon name="AlertCircle" size={20} className="text-warning" />
            </div>
            <div>
              <p className="caption text-muted-foreground">
                {t('pilgrimProfile.remainingBalance')}
              </p>
              <p className="text-xl md:text-2xl font-bold text-warning data-text">
                {formatCurrency(financial?.remainingBalance)}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h4 className="text-base md:text-lg font-medium text-foreground mb-4 md:mb-6 flex items-center gap-2">
          <Icon name="History" size={20} className="text-primary" />
          {t('pilgrimProfile.paymentHistory')}
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-2 md:px-4 caption text-muted-foreground font-medium">
                  {t('pilgrimProfile.date')}
                </th>
                <th className="text-left py-3 px-2 md:px-4 caption text-muted-foreground font-medium">
                  {t('pilgrimProfile.amount')}
                </th>
                <th className="text-left py-3 px-2 md:px-4 caption text-muted-foreground font-medium">
                  {t('pilgrimProfile.method')}
                </th>
                <th className="text-left py-3 px-2 md:px-4 caption text-muted-foreground font-medium">
                  {t('pilgrimProfile.reference')}
                </th>
                <th className="text-left py-3 px-2 md:px-4 caption text-muted-foreground font-medium">
                  {t('pilgrimProfile.status')}
                </th>
              </tr>
            </thead>
            <tbody>
              {financial?.paymentHistory?.map((payment) => (
                <tr key={payment?.id} className="border-b border-border last:border-0">
                  <td className="py-3 px-2 md:px-4 text-sm text-foreground">
                    {new Date(payment.date)?.toLocaleDateString('en-US')}
                  </td>
                  <td className="py-3 px-2 md:px-4 text-sm font-medium text-foreground data-text">
                    {formatCurrency(payment?.amount)}
                  </td>
                  <td className="py-3 px-2 md:px-4 text-sm text-foreground">
                    {getPaymentMethodLabel(payment?.method)}
                  </td>
                  <td className="py-3 px-2 md:px-4 text-sm text-muted-foreground data-text">
                    {payment?.reference}
                  </td>
                  <td className="py-3 px-2 md:px-4">
                    {getStatusBadge(payment?.status)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h4 className="text-base md:text-lg font-medium text-foreground mb-4 md:mb-6 flex items-center gap-2">
          <Icon name="FileText" size={20} className="text-primary" />
          {t('pilgrimProfile.invoices')}
        </h4>
        <div className="space-y-3 md:space-y-4">
          {financial?.invoices?.map((invoice) => (
            <div
              key={invoice?.id}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 md:p-4 bg-muted/50 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="FileText" size={18} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {t('pilgrimProfile.invoiceNumber')}: {invoice?.number}
                  </p>
                  <p className="caption text-muted-foreground">
                    {new Date(invoice.date)?.toLocaleDateString('en-US')} • {formatCurrency(invoice?.amount)}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                iconPosition="left"
                onClick={() => window.open(invoice?.url, '_blank')}
              >
                {t('pilgrimProfile.downloadInvoice')}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FinancialTab;